import { Component, OnInit, OnDestroy, DestroyRef, signal, AfterViewInit, ElementRef, computed, inject, viewChild } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { Router, ActivatedRoute } from "@angular/router";
import { ConferenceMode, IEndpoint } from "@auvious/rtc";
import { debug } from "../../app.utils";
import {
  ActivityIndicatorService,
  AppConfigService,
  IConversationEventHandlers,
  IConversationBase,
  ConversationOriginEnum,
  FEATURES,
  fadeInOut,
  slideIn,
  slideInOut,
  isDebugBetaOn,
  IApplication,
  AuviousRtcService,
  debugError,
  AgentParam,
  LocalMediaService,
  GenericErrorHandler,
  AppointmentService,
  ConferenceService,
} from "../../../core-ui";
import {
  ApplicationTypeEnum,
  ConversationChannelEnum,
  ConversationTypeEnum,
  UserRoleEnum,
} from "../../../core-ui/core-ui.enums";
import { IInteraction } from "../../../core-ui/models/IInteraction";
import { ApplicationService } from "../../../core-ui/services/application.service";
import { UserService } from "../../../core-ui/services/user.service";
import { IUserDetails, IUser } from "../../../core-ui/models/IUser";
import { NotificationService } from "../../../core-ui/services/notification.service";
import { AnalyticsService } from "../../../core-ui/services/analytics.service";
import { IntegrationService } from "../../services";
import { DeviceService } from "../../../core-ui/services/device.service";
import { InteractionService } from "../../services/interaction.service";
import { InvitationService } from "../../services/invitation.service";
import { Interaction } from "../../models/Interaction";
import { ScheduleTypeEnum } from "../../../core-ui/core-ui.enums";
import { WindowService } from "../../../core-ui/services/window.service";
import {
  ErrorPageCode,
  PARAM_ROOM_ID,
  PARAM_CONVERSATION_ID,
  PARAM_GUEST,
} from "../../app.enums";
import { CobrowseRequestType } from "@auvious/integrations";
import { CobrowseService } from "../../../core-ui/services/cobrowse.service";
import { Util } from "@auvious/common";

// eslint-disable-next-line no-shadow
enum actionsEnum {
  callback = "callback",
  cobrowse = "cobrowse",
  displayCapture = "displayCapture",
  aiAssistant = "aiAssistant",
}

const POP_OUT_TITLE = "Auvious";

@Component({
  selector: "app-page-welcome",
  templateUrl: "./welcome.component.html",
  styleUrls: ["./welcome.component.scss"],
  animations: [fadeInOut, slideIn, slideInOut],
  standalone: false,
})
export class WelcomePageComponent implements OnInit, AfterViewInit, OnDestroy {
  private router = inject(Router);
  private rtcService = inject(AuviousRtcService);
  private applicationService = inject(ApplicationService);
  private interactionService = inject(InteractionService);
  private activity = inject(ActivityIndicatorService);
  private coreConfig = inject(AppConfigService);
  private route = inject(ActivatedRoute);
  private userService = inject(UserService);
  private config = inject(AppConfigService);
  private notificationService = inject(NotificationService);
  private integration = inject(IntegrationService);
  private device = inject(DeviceService);
  private invitation = inject(InvitationService);
  private analyticsService = inject(AnalyticsService);
  private conferenceService = inject(ConferenceService);
  private mediaService = inject(LocalMediaService);
  private windowService = inject(WindowService);
  private errorHandler = inject(GenericErrorHandler);
  private cobrowseService = inject(CobrowseService);
  private destroyRef = inject(DestroyRef);

  user: IUser;
  userDetails: IUserDetails;
  interaction = signal<IInteraction>(undefined);
  conv: IConversationBase;
  activeAction = signal<actionsEnum>(undefined);
  transferMap: Map<string, boolean> = new Map();
  isReloadAvailable = false;
  isConnectionDown = false;
  isConversationEndingReceived = false;
  cobrowseRequestType = signal<CobrowseRequestType>(undefined);
  iconActive = signal(undefined);
  isCallLoading = signal(false);
  isAssistantOpen = computed(
    () => this.activeAction() === actionsEnum.aiAssistant
  );
  isAssistantAvailable = signal(false);

  private handlers: IConversationEventHandlers;
  private childPopupWindow: Window;
  private childPopupDetectionTimer;
  private _loading: boolean;

  readonly callButtonRef = viewChild<ElementRef<HTMLButtonElement>>("callBtn");

  constructor() {
    this.loading = false;
    this.route.data.subscribe((data) => {
      this.interaction.set(data.interaction);
    });
    if (this.device.isPopUp) {
      const parent = window.self;
      parent.opener = window.self;
      parent.close();
    }
    this.isReloadAvailable =
      (this.application.getType() === ApplicationTypeEnum.TalkdeskOpenID ||
        this.config.agentParamEnabled(
          AgentParam.INTERACTION_MANUAL_SYNC_ENABLED,
        )) &&
      this.application.supportsInteractions();

    this.isAssistantAvailable.set(
      this.config.agentParamEnabled(AgentParam.AI_ASSISTANT_ENABLED)
    );
  }

  async ngOnInit() {
    this.errorHandler.connectionError$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((_) => {
      this.isConnectionDown = true;
    });
    this.rtcService.connectionRegistered$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((_) => {
      this.isConnectionDown = false;
    });
    try {
      // this.childPopupWindow = null;
      this.user = this.userService.getActiveUser();
      this.userDetails = this.userService.getUserDetails();
      if (
        this.applicationService.getActiveApplication().supportsInteractions() &&
        (this.isVideoCallAvailable ||
          this.isCobrowseAvailable ||
          this.isScreenShareAvailable)
      ) {
        this.subscribeToConversationEvents();
      }

      if (!this.interaction()) {
        return;
      }

      this.setConversation(this.interaction());

      // discard for callbacks if not enabled
      if (
        (this.interaction().getType() === ConversationTypeEnum.callback &&
          !this.isSchedulingAvailable) ||
        !this.isVideoCallAvailable
      ) {
        return;
      }

      if (this.windowService.isWindowPopedOut) {
        this.childPopupWindow = window.open("", POP_OUT_TITLE);
        this.detectPopOutClose();
      } else {
        this.tryAutoStartCall();
      }
    } catch (ex) {
      // console.log(ex);
    }
  }

  ngAfterViewInit(): void {
    this.callButtonRef()?.nativeElement.focus();
  }

  ngOnDestroy() {
    this.handlers = null;
    if (this.childPopupDetectionTimer) {
      clearInterval(this.childPopupDetectionTimer);
    }
  }

  private tryAutoStartCall() {
    if (
      // this will lead to a 'room-not-found' for the agent
      // most probably the agent ended the call and the conversation has not yet transitioned to wrap-up code
      [
        ConversationOriginEnum.APPOINTMENT,
        ConversationOriginEnum.CHAT,
        ConversationOriginEnum.PHONE,
      ].includes(this.interaction().getOrigin()) &&
      !this.interaction().hasRoom()
    ) {
      return;
    }
    if (
      this.config.agentParam(AgentParam.VIDEO_CALL_ENABLED) &&
      this.config.agentParam(AgentParam.AUTO_ANSWER)
    ) {
      this.startCall();
    }
  }

  private setConversation(interaction: IInteraction) {
    // create base object, same as if we got a started event.
    this.conv = {
      conversationId: interaction.getId(),
      origin: interaction.getOrigin(),
      type: interaction.getType(),
      room: interaction.getRoom(),
      channel: interaction.getChannel(),
    };
  }

  private subscribeToConversationEvents() {
    this.interactionService.eventClient.alerted$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (interactionId: string) => {
        debug("RING! new interaction");
      }
    );

    this.interactionService.eventClient.agentJoined$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (name: string) => {
        if (!!name) {
          this.userService.setUserDetails({
            ...this.userDetails,
            displayName: name,
          });
        }
      }
    );

    this.interactionService.eventClient.started$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      async (conversation: IConversationBase) => {
        this.isConversationEndingReceived = false;
        debug("conversation started ", conversation);
        if (
          (!this.isSchedulingAvailable &&
            !!conversation &&
            conversation.type === ConversationTypeEnum.callback) ||
          this.isCallPopedOut ||
          (this.interaction()?.getOrigin() ===
            ConversationOriginEnum.APPOINTMENT &&
            !this.interaction().hasRoom())
        ) {
          return;
        }

        try {
          const interaction =
            await this.interactionService.retrieveInteractionData(
              conversation.conversationId,
              conversation.channel
            );
          if (!interaction) {
            return;
          }
          const existingInteraction =
            this.interactionService.getActiveInteraction();

          if (
            interaction.getType() === ConversationTypeEnum.chat &&
            !interaction.hasRoom() &&
            this.isCobrowseVisible()
          ) {
            const room =
              existingInteraction?.getType() === ConversationTypeEnum.cobrowse
                ? existingInteraction.getRoom()
                : this.cobrowseService.room;

            interaction.setRoom(room);

            await this.rtcService.SetConferenceInteraction(
              room,
              interaction.getId()
            );
            interaction.setRoom(room);
          }

          this.interactionService.setActiveInteraction(interaction);
          this.setConversation(interaction);
          this.interaction.set(interaction);

          if (this.interaction().getType() === ConversationTypeEnum.chat) {
            this.analyticsService.trackCallRequested(this.interaction(), {
              persistOnIntegration: false,
            });
          }

          this.tryAutoStartCall();
        } catch (ex) {
          this.notifyEmptyInteraction();
        }
      }
    );

    this.interactionService.eventClient.ending$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (interactionId: string) => {
        this.transferMap.set(interactionId, false);

        this.isConversationEndingReceived = true;

        if (this.interaction()?.getType() === ConversationTypeEnum.chat) {
          this.analyticsService.trackCallEnded(this.interaction(), false);
        }
        this.reset(interactionId);
      }
    );

    this.interactionService.eventClient.agentHeld$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (interactionId: string) => {
        this.reset(interactionId);
      }
    );

    this.interactionService.eventClient.ended$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (interactionId: string) => {
        if (
          this.interaction()?.getType() === ConversationTypeEnum.chat &&
          !this.isConversationEndingReceived
        ) {
          this.analyticsService.trackCallEnded(this.interaction(), false);
          this.isConversationEndingReceived = true;
        }
        this.reset(interactionId);
      }
    );

    this.interactionService.eventClient.transferring$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (interactionId: string) => {
        this.reset(interactionId);
      }
    );

    this.interactionService.eventClient.transferred$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (interactionId: string) => {
        this.reset(interactionId);
      }
    );
  }

  private reset(interactionId?: string) {
    this.conv = null;
    this.interaction.set(null);
    if (
      this.interactionService.getActiveInteraction()?.getId() === interactionId
    ) {
      this.interactionService.clearActiveInteraction();
    }
  }

  private get application(): IApplication {
    return this.applicationService.getActiveApplication();
  }

  private notifyEmptyInteraction() {
    this.notificationService.warn("Auvious", {
      body: "Could not retrieve interaction details. Try refreshing your page.",
    });
  }

  onMouseDown(target: string) {
    this.iconActive.set(target);
  }

  onMouseUp() {
    this.iconActive.set(undefined);
  }

  async startCall() {
    if (!this.isCallEnabled || this.isCallPopedOut || this.isCallLoading()) {
      return;
    }
    try {
      this.isCallLoading.set(true);
      if (this.interactionService.isActiveInteractionAvailable()) {
        await this.joinRoomWithInteraction(
          this.interactionService.getActiveInteraction()
        );
      } else if (this.application.canCallWithoutActiveInteraction()) {
        await this.joinRoomWithoutInteraction();
      }
    } catch (error) {
      debugError(error);
      this.notificationService.error(error.message || error);
    } finally {
      this.isCallLoading.set(false);
    }
  }

  async joinRoomWithoutInteraction(): Promise<void> {
    try {
      const id = Util.uuidgen();
      const conference = await this.rtcService.createConference({
        mode: ConferenceMode.ROUTER,
        interactionId: id,
        id,
      });
      if (conference) {
        const interaction = new Interaction(conference.name, id);
        interaction.setAgentId(this.user.getId());
        this.interactionService.setActiveInteraction(interaction);
        this.conferenceService.setActiveConference(conference);
        await this.joinRoom(conference.name, interaction);
      } else {
        throw new Error("Could not create conference.");
      }
    } catch (ex) {
      this.notificationService.error("Error", {
        body: ex.body?.message || ex.message || ex,
      });
      if (ex.response?.status === 401) {
        this.router.navigate(["/error", ErrorPageCode.GENERIC_ERROR]);
      }
      debugError(ex);
    }
  }

  async joinRoomWithInteraction(interaction: IInteraction): Promise<void> {
    try {
      if (interaction.hasRoom()) {
        await this.rtcService.SetConferenceInteraction(
          interaction.getRoom(),
          interaction.getId()
        );
        await this.joinRoom(interaction.getRoom(), interaction);
      } else {
        const conference = await this.rtcService.createConference({
          mode: ConferenceMode.ROUTER,
          interactionId: interaction.getId(),
        });
        interaction.setRoom(conference.name);
        // update the active interaction with the new room name;
        this.interactionService.setActiveInteraction(interaction);
        this.conferenceService.setActiveConference(conference);
        await this.joinRoom(interaction.getRoom(), interaction);
      }
    } catch (ex) {
      this.notificationService.error("Error", {
        body: ex.body?.message || ex.message || ex,
      });
      if (ex.response?.status === 401) {
        this.router.navigate(["/error", ErrorPageCode.GENERIC_ERROR]);
      }
      debugError(ex);
    }
  }

  toggleCallback() {
    switch (this.config.agentParam(AgentParam.SCHEDULE_CHANNEL)) {
      case ScheduleTypeEnum.genesysCallback:
        this.activeAction.set(
          this.activeAction() === actionsEnum.callback
            ? null
            : actionsEnum.callback
        );
        break;
      case ScheduleTypeEnum.appointment:
        this.router.navigate(["/schedule"], { queryParamsHandling: "merge" });
        break;
    }
  }

  toggleCobrowse() {
    this.activeAction.set(actionsEnum.cobrowse);
    this.cobrowseRequestType.set("view");
  }

  toggleDisplayCapture() {
    this.activeAction.set(actionsEnum.displayCapture);
    this.cobrowseRequestType.set("display-capture");
  }

  cobrowseCancelled() {
    this.activeAction.set(undefined);
    this.cobrowseRequestType.set(undefined);

    // clear interaction
    if (
      this.interactionService.getActiveInteraction()?.getType() ===
      ConversationTypeEnum.cobrowse
    ) {
      this.interactionService.clearActiveInteraction();
    }
  }

  cobrowseStarted(payload: {
    participant: IEndpoint;
    interaction: IInteraction;
  }) {
    this.activeAction.set(null);
    // this.activity.loading(true, 'Waiting for approval');
    // this.interaction = payload.interaction;
    // this.remoteCobrowseParticipant = payload.participant;
    this.router.navigate(["/", "cobrowse"], {
      queryParamsHandling: "preserve",
    });
  }

  joinRoom(room: string, interaction: IInteraction) {
    if (this.config.agentParamEnabled(AgentParam.POP_OUT_CALL)) {
      // if (this.childPopupWindow) { return; }
      // open as a popup
      try {
        const urlString = this.invitation.prepareAgentInvitation(interaction);
        const url = new URL(urlString);
        url.searchParams.delete(PARAM_GUEST);

        this.childPopupWindow = window.open(
          url.toString(),
          POP_OUT_TITLE,
          "height=600,width=900"
        );
        this.windowService.isWindowPopedOut = true;
        //  detect window close
        this.detectPopOutClose();
      } catch (ex) {
        this.notificationService.error("Bad URL", {
          body: "Could not create meeting url",
        });
      }
    } else {
      // open in same page
      this.router.navigate(["/a"], {
        queryParams: {
          [PARAM_ROOM_ID]: room,
          [PARAM_CONVERSATION_ID]: interaction?.getId(),
        },
        queryParamsHandling: "merge",
      });
    }
  }

  private detectPopOutClose() {
    this.childPopupDetectionTimer = setInterval(async () => {
      if (this.childPopupWindow?.closed) {
        clearInterval(this.childPopupDetectionTimer);
        this.windowService.isWindowPopedOut = false;
        this.childPopupWindow = null;
        // todo: do the same for appointment but only if the customer has joined the room (otherwise the agent leaves)
        if (
          this.interaction() &&
          this.interaction().getType() !== ConversationTypeEnum.callback
        ) {
          this.interaction.update((i) => i.setCustomerInvited(false));
          await this.analyticsService.trackCallEnding(this.interaction());
          this.interaction.set(
            await this.interactionService.retrieveInteractionData(
              this.interaction().getId()
            )
          );
          this.interactionService.setActiveInteraction(this.interaction());
        }
      }
    }, 1000);
  }

  async getInteractions() {
    this.activity.loading(true);
    this.interaction.set(
      await this.interactionService.discoverActiveInteraction()
    );

    !!this.interaction()
      ? this.setConversation(this.interaction())
      : (this.conv = undefined);
    this.activity.loading(false);
  }

  openAssistant() {
    this.activeAction.set(actionsEnum.aiAssistant);
  }

  assistantClosed() {
    this.activeAction.set(undefined);
  }

  get videoCallRequiresActiveInteraction() {
    return this.config.agentParamEnabled(
      AgentParam.VIDEO_CALL_REQUIRES_INTERACTION
    );
  }

  get name() {
    return (this.userDetails && this.userDetails.name) || "";
  }

  get isDebugBeta() {
    return isDebugBetaOn();
  }

  get isConversationAudio() {
    return this.conv?.type === ConversationTypeEnum.voiceCall;
  }

  get isConversationVideo() {
    return this.conv?.type === ConversationTypeEnum.videoCall;
  }

  get isConversationChat() {
    return this.conv?.type === ConversationTypeEnum.chat;
  }

  get isOriginWidget() {
    return this.conv?.origin === ConversationOriginEnum.WIDGET;
  }

  get isOriginAppointment() {
    return this.conv?.origin === ConversationOriginEnum.APPOINTMENT;
  }

  get isOriginCall() {
    return this.conv?.origin === ConversationOriginEnum.PHONE;
  }

  get isOriginChat() {
    return (
      !this.coreConfig.featureEnabled(FEATURES.WIDGET_ENABLED) ||
      (this.conv && this.conv.origin === ConversationOriginEnum.CHAT)
    );
  }

  // get isDestinationInteractionWidget() { return this.interaction?.getDestination() === ConversationDestinationEnum.INTERACTION_WIDGET; }

  get isConversationCallback() {
    return this.conv && this.conv.type === ConversationTypeEnum.callback;
  }

  isActiveActionEmpty = computed(() => !this.activeAction());

  get isSchedulingAvailable() {
    return (
      (this.integration.supportsInteractionOrigin(
        ConversationOriginEnum.CALLBACK
      ) ||
        this.integration.supportsInteractionOrigin(
          ConversationOriginEnum.APPOINTMENT
        )) &&
      this.config.agentParamEnabled(AgentParam.SCHEDULE_ENABLED)
    );
  }

  get isSchedulingDisabled() {
    return this.isConnectionDown || this.isSchedulingNotConfigured;
  }

  get isSchedulingNotConfigured() {
    return (
      // we default to genesysCallback so we need to go to settings to auto-update
      this.application.getType() == ApplicationTypeEnum.TalkdeskOpenID &&
      this.config.agentParam(AgentParam.SCHEDULE_CHANNEL) ===
        ScheduleTypeEnum.genesysCallback
    );
  }

  isCallbackVisible = computed(
    () => this.activeAction() === actionsEnum.callback
  );

  isCobrowseVisible = computed(() =>
    [actionsEnum.cobrowse, actionsEnum.displayCapture].includes(
      this.activeAction()
    )
  );

  get isCobrowseAvailable() {
    return (
      this.config.agentParamEnabled(AgentParam.COBROWSE_ENABLED) &&
      this.integration.hasLicense("cobrowse")
    );
  }

  get isScreenShareAvailable() {
    return (
      this.config.agentParamEnabled(AgentParam.SCREEN_SHARE_WIDGET_ENABLED) &&
      this.integration.hasLicense("screenshare")
    );
  }

  get isCobrowseDisabled() {
    const supportsInteractions = this.applicationService
      .getActiveApplication()
      .supportsInteractions();

    return (
      ((supportsInteractions &&
        this.config.agentParamEnabled(AgentParam.COBROWSE_INTERACTION_AWARE)) ||
        !supportsInteractions) &&
      (this.isConnectionDown ||
        this.isConversationAudio ||
        (this.isConversationVideo &&
          !(
            this.isOriginCall ||
            this.interaction()?.getChannel() ===
              ConversationChannelEnum.genesysCall
          )))
    );
  }

  isActionEnabled = computed(
    () => !this.isCobrowseVisible() && !this.isCallbackVisible()
  );

  get isInteractionAvailable() {
    return (
      this.isOriginCall ||
      this.isOriginWidget ||
      this.isOriginChat ||
      this.isConversationCallback ||
      this.isOriginAppointment
    );
  }

  get isInitialized() {
    return !!this.application && this.application.isReady();
  }

  get isCallEnabled(): boolean {
    if (this.isConnectionDown) {
      return false;
    }
    if (!!this.application) {
      if (this.application.supportsInteractions()) {
        return this.videoCallRequiresActiveInteraction
          ? this.isInteractionAvailable
          : true;
      } else {
        return true;
      }
    }
  }

  get isApplicationConfigured() {
    return !!this.application.getId();
  }

  get isConfigEnabled() {
    return this.user.hasRole(UserRoleEnum.admin);
  }

  get isRecordingEnabled() {
    return this.user.hasRole(UserRoleEnum.supervisor);
  }

  public get loading(): boolean {
    return this._loading;
  }

  public set loading(value: boolean) {
    this._loading = value;
    this.activity.loading(value);
  }

  get isVideoCallAvailable() {
    return (
      this.config.agentParamEnabled(AgentParam.VIDEO_CALL_ENABLED) &&
      this.integration.hasLicense("video")
    );
  }

  get isHelpVisible() {
    return (
      !this.isVideoCallAvailable &&
      !this.isCobrowseAvailable &&
      !this.isSchedulingAvailable &&
      !this.isScreenShareAvailable
    );
  }

  get isCallPopedOut() {
    return this.windowService.isWindowPopedOut;
  }
}
